import React from 'react';
import { render } from 'react-dom';
import { browserHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import throttle from 'lodash/throttle';
import { getLocaleForAppdomain } from '@config/translate';
import { getConfigForAppdomain } from '@config';
import { isSupported as isWebPushSupported } from '@client/helpers/pushNotification/management';
import { requestWebPushExperiment } from '@shared/abtests/webPush';
import {
    fetchRegistrationForNotification,
    fetchNotificationSupported,
    fetchNotificationPermission,
    fetchOptedInForNotification
} from '@client/redux/modules/pushNotification/actions';

import replaceWith from './helpers/replaceWith';
import { init as initSentry } from './helpers/sentry';
import {
    setClientDimensions,
    setClientOffset
} from './helpers/clientDimensions';
import {
    getVisibilityChangeEventName,
    setTabVisibility
} from './helpers/tabVisibility';
import { isIE } from './helpers/browser';
import { addClassListToSVG } from './helpers/shims';

const appdomain = (typeof window !== 'undefined' && window.APPDOMAIN) || process.env.APPDOMAIN;

const config = getConfigForAppdomain(appdomain);
const translations = getLocaleForAppdomain(appdomain);
const locale = config.locale[appdomain];

// Set up Sentry
if (typeof window !== 'undefined') {
    initSentry(
        {
            dsn: config.sentry && config.sentry.dsn,
            enabled: config.isEnabled('sentry'),
        },
        {
            appdomain,
        },
    );
}

// just require it so it gets copied on deploy by webpack
require('../favicon.ico');

import makeRoutes from './routes';
import configureStore from './redux/configureStore';
import AppRoot from './containers/AppRoot';

let initialState = {};

/* eslint-disable no-console */

// Try to re-use initial state from server side rendering on client
// as window is undefined on server, we need to wrap this into try/catch
try {
    if (typeof window !== undefined && window.__INITIAL_STATE__) {
        initialState = Object.assign({}, initialState, window.__INITIAL_STATE__);
        delete initialState.date;
        delete initialState.processingTime;
        delete window.__INITIAL_STATE__;
    }
}
catch (e) {
    console.error('catch', e);
}

// SVG classList shim for IE
if (isIE()) {
    addClassListToSVG();
}

// This is to make webpack async load from the correct place
if (!__DEV__) {
    // eslint-disable-next-line
    __webpack_public_path__ = config.getUrl('assets');
}

// Create redux store and sync with react-router-redux. We have installed the
// react-router-redux reducer under the key "router" in src/routes/index.js,
// so we need to provide a custom `selectLocationState` to inform
// react-router-redux of its location.
const store = configureStore(initialState, config);
const history = syncHistoryWithStore(browserHistory, store, {
    selectLocationState: (state) => state.router
});

// Now that we have the Redux store, we can create our routes. We provide
// the store to the route definitions so that routes have access to it for
// hooks such as `onEnter`.
const routes = makeRoutes(store, { config, translations });

const startApp = async () => {
    // in case browser does not support native Intl, polyfill it
    // see http://formatjs.io/guides/runtime-environments
    // TODO: possibly better to load only one language here?
    if (!window.Intl || !window.matchMedia) {
        window.Intl = await import(/* webpackChunkName: "intl" */ 'intl');
        await import(/* webpackChunkName: "match-media" */ 'match-media');

        if (locale && !window.Intl.PluralRules) {
            await Promise.all([
                import(/* webpackChunkName: "intl-pluralrules/polyfill" */ '@formatjs/intl-pluralrules/polyfill'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/en.js" */ '@formatjs/intl-pluralrules/dist/locale-data/en.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/fr.js" */ '@formatjs/intl-pluralrules/dist/locale-data/fr.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/de.js" */ '@formatjs/intl-pluralrules/dist/locale-data/de.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/it.js" */ '@formatjs/intl-pluralrules/dist/locale-data/it.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/nl.js" */ '@formatjs/intl-pluralrules/dist/locale-data/nl.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/pl.js" */ '@formatjs/intl-pluralrules/dist/locale-data/pl.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/es.js" */ '@formatjs/intl-pluralrules/dist/locale-data/es.js'),
                import(/* webpackChunkName: "intl/intl-pluralrules/jsonp/cz.js" */ '@formatjs/intl-pluralrules/dist/locale-data/cs.js'),
            ]);
        }
        if (!window.Intl.RelativeTimeFormat) {
            await Promise.all([
                import(/* webpackChunkName: "intl-relativetimeformat/polyfill" */ '@formatjs/intl-relativetimeformat/polyfill'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/en.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/en.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/fr.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/fr.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/de.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/de.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/it.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/it.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/nl.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/nl.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/pl.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/pl.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/es.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/es.js'),
                import(/* webpackChunkName: "intl/intl-relativetimeformat/jsonp/cz.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/cs.js'),
            ]);
        }
    }

    // Set the client device
    if (typeof window !== 'undefined') {
        setClientDimensions(store);
        setClientOffset(store);
        setTabVisibility(store);

        window.addEventListener('resize', throttle(() => setClientDimensions(store), 200));
        window.addEventListener('scroll', throttle(() => setClientOffset(store), 400));
        document.addEventListener(getVisibilityChangeEventName(), () => setTabVisibility(store));
    }

    if (typeof window !== 'undefined' && config.isEnabled('pushNotification')) {
        store.dispatch(fetchNotificationSupported());
        store.dispatch(fetchRegistrationForNotification());
        store.dispatch(fetchNotificationPermission());
        store.dispatch(fetchOptedInForNotification());
        isWebPushSupported().then((webPushSupported) => {
            if (webPushSupported) {
                store.dispatch(requestWebPushExperiment());
            }
        });
    }

    // client side rendering will mount the app into #react-container
    // runtime errors in dev will show redbox overlay with stack trace
    const root = document.getElementById('react-container');
    if (__DEV__) {
        const RedBox = require('redbox-react');
        try {
            render(<AppRoot store={store} history={history} routes={routes} translations={translations} config={config} />, root);
        }
        catch (e) {
            console.error(e);
            render(<RedBox error={e} />, root);
        }
    }
    else {
        render(<AppRoot store={store} history={history} routes={routes} translations={translations} config={config} />, root);
    }
};

if (!Element.prototype.replaceWith) {
    Element.prototype.replaceWith = replaceWith;
}
if (!CharacterData.prototype.replaceWith) {
    CharacterData.prototype.replaceWith = replaceWith;
}
if (!DocumentType.prototype.replaceWith) {
    DocumentType.prototype.replaceWith = replaceWith;
}

startApp();
